import React, {
    FC,
    useContext, useCallback,
    useMemo,
    useState,
    useRef,
    useEffect,
} from 'react';
import { gql, useFragment, useReactiveVar } from '@apollo/client';
import classNames from 'classnames';

import { LocaleContext } from 'app/containers/LocaleProvider';

import {
    getImageUrl, menuImageSize,
    menuImageSizeMobile,
} from 'app/utils/dish';

import { subscriptionTypeVar } from 'app/apollo/reaction';
import { OVEN_TAG_ID } from 'app/utils/filters';
import { useBasketQueryLocate } from 'app/hooks/useBasketQueryLocate';
import { RENDERING_CONTEXT_MENU, useDishDetails } from '../../MobileDishDetails';


import { PortionsButton } from '../../ui/BR1258/PortionsButton/PortionsButton';
import { TwoLineText } from '../TwoLineText/TwoLineText';
import MobileDishImageFC from '../../MobileDish/MobileDishImage';

import XIcon from './img/x.component.svg';
import css from './DishCardSmall.module.scss';

const locales: any = {
    ru: {
        weight: 'г',
        min: 'мин',
        ready: 'Уже готово',
    },
    en: {
        weight: 'g',
        min: 'min',
        ready: 'Уже готово',
    },
};

export const DishCardSmall: FC = (props: any) => {
    const {
        id,
        weight,
        nutrition,
        categoryId,
        portions,
        price,
        onOpenDetails,
        spaDishObserver,
        title,
        caption = null,
        cooking_time: cookingTime = 0,
        cook_until: cookUntil,
        umAlias = ['г', 'г', 'г', 'г'],
        isDishCardWithCustomSelect,
        isDishCardWithCustomTabBar,
        isDishWithAdditionalDishesSelect,
        ingredientsList,
        selectedDishId,
        selectedDishPortions,
        onNextIdSelected,
        optionTags,
        additionalDescription,
        previewMobileImage,
        previewImage,
        verticalImage,
        previewMobileAlterImage,
        useLazyLoading,
        isGreen,
        isHits,
        isSmall,
        isShop,
        isSingleItem,
        isUpsell,
        isAlterImageUsed = false,
        isPriceHidden = false,
        canApplySoldOutStyles = false,
        scrollPosition = null,
        isAutoscrollingDisabled = false,
        titleEn = '',
        captionEn = '',
        ...restProps
    } = props;

    const rootRef = useRef(null);

    const {
        fat, calories, carbohydrate, protein,
    } = nutrition;

    const [isNeededRenderStub, setIsNeededRenderStub] = useState(false);
    const subscriptionType = useReactiveVar(subscriptionTypeVar);

    const [basketData] = useBasketQueryLocate();
    const isDishAdded = useMemo(
        () => {
            const flatSection = basketData?.cart?.sections
                .reduce((acc: any[], section: any) => [...acc, ...section.items], [])
                .map((cartItem: any) => cartItem.dish_id);
            return flatSection?.includes(id) || false;
        },
        [id, basketData],
    );

    const [showWholeButton, setShowWholeButton] = useState(isDishAdded);

    const { openDishDetails } = useDishDetails(() => { });
    const { locale } = useContext(LocaleContext);

    const { data: dishData } = useFragment({
        fragment: gql`fragment DISH_DESCRIPTION on menuDish {
            additionalDescription
            tags {
                id
            }
        }`,
        from: {
            __ref: `menuDish:${selectedDishId}`,
        },
    });

    const cookingTimeInMin = Math.round(cookingTime / 60);

    const isRecurrent = useMemo(
        () => subscriptionType === 'subscription',
        [subscriptionType],
    );

    const isCookedInOven = useMemo(
        () => dishData?.tags?.some((tag: any) => tag.id === OVEN_TAG_ID),
        [dishData],
    );

    const handleOpenDetails = useCallback(() => {
        openDishDetails(id, RENDERING_CONTEXT_MENU);
        if (onOpenDetails) {
            onOpenDetails(id);
        }
    }, [id, openDishDetails, onOpenDetails]);

    const imageUrl = getImageUrl({
        url: isGreen
            ? verticalImage
            : previewImage,
        ...menuImageSize,
    });

    const currentImage = useMemo(
        () => {
            if (isAlterImageUsed && isUpsell) {
                return previewMobileAlterImage || previewMobileImage;
            }
            if (isAlterImageUsed) {
                return previewMobileAlterImage;
            }
            if (isGreen || isHits) {
                return verticalImage;
            }
            return previewMobileImage;
        },
        [
            isAlterImageUsed, isGreen, isHits,
            previewMobileAlterImage, previewMobileImage, verticalImage, isUpsell,
        ],
    );
    const imageUrlMobile = getImageUrl({
        url: currentImage,
        ...menuImageSizeMobile,
    });

    const handleImageLoaded = useCallback(
        () => {
            setIsNeededRenderStub(false);
        },
        [],
    );

    const rootClasses = classNames({
        [css.dishCardSmallRoot]: true,
        [css.shop]: isShop,
        [css.singleItem]: isSingleItem,
        [css.upsell]: isUpsell,
    });

    const imageClasses = classNames({
        [css.dishCardSmallImg]: true,
        [css.small]: isSmall,
        [css.shop]: isShop,
        [css.singleItem]: isSingleItem,
        [css.upsell]: isUpsell,
    });

    const buttonWrapperClasses = classNames({
        [css.dishCardSmallAddButtonWrapper]: true,
        [css.full]: true,
        [css.singleItem]: isSingleItem,
        [css.upsell]: isUpsell,
        [css.added]: showWholeButton,
    });

    useEffect(
        () => {
            if (rootRef.current) {
                spaDishObserver.observe(rootRef.current);
            }
        },
        // eslint-disable-next-line
        [rootRef.current],
    );

    return (
        <div
            className={rootClasses}
            data-id={id}
            onClick={handleOpenDetails}
            role="none"
            ref={rootRef}
        >
            <div className={imageClasses}>
                {!isNeededRenderStub && (
                    <div className={`${css.imageWrapper} ${isGreen && `${css.green}`}`}>
                        <MobileDishImageFC
                            id={id}
                            url={imageUrlMobile}
                            fallbackUrl={imageUrl}
                            alt={title}
                            scrollPosition={scrollPosition}
                            useLazyLoading={useLazyLoading}
                            onLoadError={handleImageLoaded}
                            onLoad={handleImageLoaded}
                        />
                    </div>
                )}
                {isGreen && !isDishAdded && (
                    <div className={css.dishCardSmallAdditinalInfoWrapper}>
                        <div className={`${css.dishCardSmallAdditinalInfoShield} ${css.green}`}>
                            <span className={`other-number-xxs-regular ${css.dishCardSmallAdditionalDigit} ${css.green}`}>1</span>
                            {' '}
                            <span className={`other-shield-default-medium ${css.dishCardSmallAdditionalText} ${css.green}`}>порция</span>
                            <div className={css.dishCardSmallAdditionalInfoLineDivider}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="2" height="2" viewBox="0 0 2 2" fill="none">
                                    <circle cx="1" cy="1" r="1" fill="#DAF7C7" />
                                </svg>
                            </div>
                            <span className={`other-number-xxs-regular ${css.dishCardSmallAdditionalDigit} ${css.green}`}>
                                {Math.round((calories.value * weight) / 100)}
                            </span>
                            {' '}
                            <span className={`other-shield-default-medium ${css.dishCardSmallAdditionalText} ${css.green}`}>ккал</span>
                        </div>

                        <div className={css.dishCardSmallAdditinalInfoText}>
                            <div className={css.dishCardSmallNutritionItem}>
                                <span className={`body-xxs-medium ${css.dishCardSmallAdditionalTextSmall}`}>Б</span>
                                <span className={`other-number-xxs-regular ${css.dishCardSmallAdditionalDigitSmall}`}>
                                    {Math.round((protein.value * weight) / 100)}
                                </span>
                            </div>
                            <div className={css.dishCardSmallAdditionalInfoLineDivider}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="2" height="2" viewBox="0 0 2 2" fill="none">
                                    <circle cx="1" cy="1" r="1" fill="#DAF7C7" />
                                </svg>
                            </div>
                            <div className={css.dishCardSmallNutritionItem}>
                                <span className={`body-xxs-medium ${css.dishCardSmallAdditionalTextSmall}`}>Ж</span>
                                <span className={`other-number-xxs-regular ${css.dishCardSmallAdditionalDigitSmall}`}>
                                    {Math.round((fat.value * weight) / 100)}
                                </span>
                            </div>
                            <div className={css.dishCardSmallAdditionalInfoLineDivider}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="2" height="2" viewBox="0 0 2 2" fill="none">
                                    <circle cx="1" cy="1" r="1" fill="#DAF7C7" />
                                </svg>
                            </div>
                            <div className={css.dishCardSmallNutritionItem}>
                                <span className={`body-xxs-medium ${css.dishCardSmallAdditionalTextSmall}`}>У</span>
                                <span className={`other-number-xxs-regular ${css.dishCardSmallAdditionalDigitSmall}`}>
                                    {Math.round((carbohydrate.value * weight) / 100)}
                                </span>
                            </div>
                        </div>
                    </div>
                )}
                {isHits && !isDishAdded && (
                    <div className={`${css.dishCardSmallAdditinalInfoWrapper} ${isCookedInOven ? '' : css.noOven}`}>
                        <div className={`${css.dishCardSmallAdditinalInfoShield} ${css.white}`}>
                            <span className={`other-shield-default-medium ${css.dishCardSmallAdditionalText} ${css.grey}`}>готовить</span>
                            <span className={`other-number-xxs-regular ${css.dishCardSmallAdditionalDigit} ${css.grey}`}>
                                {cookingTimeInMin}
                            </span>
                            {' '}
                            <span className={`other-shield-default-medium ${css.dishCardSmallAdditionalText} ${css.grey}`}>минут</span>
                        </div>
                        {isCookedInOven && (
                            <div className={css.dishCardSmallAdditinalInfoText}>
                                <span className={`other-shield-default-medium ${css.dishCardSmallAdditionalTextSmall} ${css.grey}`}>
                                    в духовке
                                </span>
                            </div>
                        )}
                    </div>
                )}
                {isGreen && <div className={css.dishCardSmallGradientBlock} />}
                {isHits && <div className={`${css.dishCardSmallGradientBlock} ${css.white}`} />}
                <div
                    className={buttonWrapperClasses}
                    onClick={(e) => e.stopPropagation()}
                    role="none"
                >
                    <PortionsButton
                        portions={portions}
                        selectedDishPortions={selectedDishPortions}
                        price={price}
                        dishId={id}
                        isPriceHidden={isPriceHidden || isSingleItem}
                        isUpsell={isUpsell}
                        isHidden
                        selectedDishId={selectedDishId}
                        isDishWithAdditionalDishesSelect={isDishWithAdditionalDishesSelect
                            && !isDishCardWithCustomTabBar && !isDishCardWithCustomSelect}
                        serviceCallback={setShowWholeButton}
                    />
                </div>
            </div>
            <div className={css.dishCardSmallInfoContainer}>
                <div className={css.dishCardSmallTextContainer}>
                    <span className={`other-number-s-medium ${css.dishCardSmallTextDigits}`}>
                        {isRecurrent
                            ? Math.ceil(price * 0.9)
                            : price
                        }
                    </span>
                    <span className={`body-s-semibold ${css.dishCardSmallTextLetters}`}>₽</span>
                    {!isShop && !isSingleItem && (
                        <>
                            <XIcon className={css.dishCardSmallXIcon} />
                            <span className={`other-number-s-medium ${css.dishCardSmallTextDigits} ${css.grey}`}>{portions[0]}</span>
                        </>
                    )}
                    {(isShop || isSingleItem) && (
                        <>
                            <div className={css.dishCardSmallAdditionalInfoLineDivider}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="2" height="2" viewBox="0 0 2 2" fill="none">
                                    <circle cx="1" cy="1" r="1" fill="#B7B7B7" />
                                </svg>
                            </div>
                            <span className={`other-number-s-medium ${css.dishCardSmallTextDigits} ${css.grey}`}>
                                {weight}
                            </span>
                            {' '}
                            <span className={`body-s-regular ${css.dishCardSmallTextLetters} ${css.grey}`}>
                                {locales[locale].weight}
                            </span>
                        </>
                    )}
                </div>
                <div className={css.dishCardSmallDishTitleContainer}>
                    <TwoLineText
                        title={title}
                        caption={caption}
                        titleClass={`body-s-medium ${css.dishCardSmallDishTitle}`}
                        numberOfLines={isUpsell ? 3 : 2}
                    />
                </div>
            </div>
        </div>
    );
};
